<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { LANDING_CONTENT } from '@/services/landing-content';
import { useUserStore } from '@/store/user-store';

const props = defineProps({
  setAuth: Function
});

const userStore = useUserStore();

// Use retail content directly
const heroContent = LANDING_CONTENT.retail.hero;

const handlePrimaryCTA = () => {
  userStore.setAuthView('signup');
  props.setAuth?.(true);
};

const handleSecondaryCTA = () => {
  // Navigate to "Discover Your Investment Strategy" (Risk Assessment) section
  const riskSection = document.getElementById('risk-assessment');
  if (riskSection) {
    riskSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

// Carousel state
const currentSlide = ref(0);
const totalSlides = 3;
let carouselInterval = null;

const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % totalSlides;
};

const goToSlide = (index) => {
  currentSlide.value = index;
};

onMounted(() => {
  // Auto-advance carousel every 3 seconds (3000ms feels better than 500ms for readability)
  carouselInterval = setInterval(nextSlide, 3000);
});

onUnmounted(() => {
  if (carouselInterval) {
    clearInterval(carouselInterval);
  }
});
</script>

<template>
  <section class="hero-section">
    <div class="hero-container">
      <div class="hero-content">
        <h1 class="hero-headline">{{ heroContent.headline }}</h1>
        <p class="hero-subheadline">{{ heroContent.subheadline }}</p>

        <div class="hero-cta-group">
          <button class="cta-primary" @click="handlePrimaryCTA">
            {{ heroContent.primaryCTA }}
            <i class="pi pi-arrow-right"></i>
          </button>
          <button class="cta-secondary" @click="handleSecondaryCTA">
            {{ heroContent.secondaryCTA }}
          </button>
        </div>

        <p class="trust-line">
          <i class="pi pi-check-circle"></i>
          {{ heroContent.trustLine }}
        </p>
      </div>

      <div class="hero-visual">
        <div class="dashboard-preview">
          <div class="preview-header">
            <div class="preview-dot"></div>
            <div class="preview-dot"></div>
            <div class="preview-dot"></div>
          </div>
          <div class="preview-content">
            <!-- Carousel Slides -->
            <div class="carousel-container">
              <!-- Slide 1: Portfolio Diversity -->
              <div class="carousel-slide" :class="{ active: currentSlide === 0 }">
                <div class="diversity-container">
                  <h3 class="diversity-title">Portfolio Diversity</h3>
                  <div class="diversity-content">
                    <div class="donut-visual"></div>
                    <div class="diversity-table">
                      <div class="table-row table-header">
                        <div class="table-cell">Asset</div>
                        <div class="table-cell">%</div>
                        <div class="table-cell">Amount</div>
                      </div>
                      <div class="table-row">
                        <div class="table-cell"><span class="color-dot" style="background: #3b82f6;"></span> Stocks</div>
                        <div class="table-cell">6.33%</div>
                        <div class="table-cell">₹5.5L</div>
                      </div>
                      <div class="table-row">
                        <div class="table-cell"><span class="color-dot" style="background: #4ade80;"></span> Mutual Funds</div>
                        <div class="table-cell">4.90%</div>
                        <div class="table-cell">₹4.3L</div>
                      </div>
                      <div class="table-row">
                        <div class="table-cell"><span class="color-dot" style="background: #a855f7;"></span> Pension</div>
                        <div class="table-cell">50.57%</div>
                        <div class="table-cell">₹43.9L</div>
                      </div>
                      <div class="table-row">
                        <div class="table-cell"><span class="color-dot" style="background: #14b8a6;"></span> Fixed Deposits</div>
                        <div class="table-cell">25.01%</div>
                        <div class="table-cell">₹22.6L</div>
                      </div>
                      <div class="table-row">
                        <div class="table-cell"><span class="color-dot" style="background: #fbbf24;"></span> Others</div>
                        <div class="table-cell">13.19%</div>
                        <div class="table-cell">₹10.6L</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>              <!-- Slide 2: Portfolio Performance -->
              <div class="carousel-slide" :class="{ active: currentSlide === 1 }">
                <div class="performance-container">
                  <h3 class="performance-title">Portfolio Market Value</h3>
                  <div class="performance-cards">
                    <div class="perf-card perf-card-primary">
                      <div class="perf-card-title">TOTAL PORTFOLIO VALUE</div>
                      <div class="perf-card-value">₹13,25,000</div>
                    </div>
                    <div class="perf-card perf-card-change">
                      <div class="perf-card-title">TODAY'S CHANGE (STOCKS + MF)</div>
                      <div class="perf-card-value perf-positive">+₹18,450 <span class="perf-pct">(+1.41%)</span></div>
                    </div>
                  </div>
                  <div class="performance-chart-title">Market-linked Value Trend</div>
                  <svg class="trend-chart" viewBox="0 0 380 130">
                    <!-- Y-axis labels -->
                    <text x="2" y="22" font-size="7" fill="#9ca3af">₹13L</text>
                    <text x="2" y="45" font-size="7" fill="#9ca3af">₹12L</text>
                    <text x="2" y="68" font-size="7" fill="#9ca3af">₹11L</text>
                    <text x="2" y="91" font-size="7" fill="#9ca3af">₹10L</text>
                    <text x="2" y="114" font-size="7" fill="#9ca3af">₹9L</text>

                    <!-- Grid lines -->
                    <line x1="30" y1="18" x2="380" y2="18" stroke="#f3f4f6" stroke-width="1"/>
                    <line x1="30" y1="41" x2="380" y2="41" stroke="#f3f4f6" stroke-width="1"/>
                    <line x1="30" y1="64" x2="380" y2="64" stroke="#f3f4f6" stroke-width="1"/>
                    <line x1="30" y1="87" x2="380" y2="87" stroke="#f3f4f6" stroke-width="1"/>
                    <line x1="30" y1="110" x2="380" y2="110" stroke="#f3f4f6" stroke-width="1"/>

                    <!-- Trend line - realistic growth with dips (80+ points, convex curve) -->
                    <polyline points="30,95 32,94 34,93 36,92 38,92 40,91 42,91 44,90 46,90 48,89 50,89 52,88 54,88 56,87 58,87 60,88 62,89 64,90 66,89 68,88 70,87 72,87 74,86 76,86 78,85 80,85 82,84 84,84 86,83 88,83 90,82 92,82 94,81 96,81 98,80 100,80 102,81 104,82 106,81 108,80 110,79 112,79 114,78 116,78 118,77 120,77 122,76 124,76 126,75 128,75 130,74 132,74 134,73 136,73 138,72 140,72 142,71 144,71 146,70 148,70 150,69 152,69 154,68 156,68 158,67 160,68 162,69 164,70 166,69 168,68 170,67 172,67 174,66 176,66 178,65 180,65 182,64 184,64 186,63 188,63 190,62 192,62 194,63 196,64 198,63 200,62 202,61 204,61 206,60 208,60 210,59 212,59 214,58 216,58 218,57 220,57 222,56 224,56 226,55 228,55 230,54 232,54 234,53 236,53 238,52 240,52 242,51 244,51 246,50 248,50 250,49 252,49 254,48 256,48 258,47 260,48 262,49 264,48 266,47 268,46 270,46 272,45 274,45 276,44 278,44 280,43 282,43 284,42 286,42 288,41 290,41 292,40 294,40 296,39 298,39 300,38 302,38 304,37 306,38 308,39 310,38 312,37 314,36 316,36 318,35 320,35 322,34 324,34 326,33 328,33 330,32 332,32 334,31 336,31 338,30 340,30 342,29 344,29 346,28 348,28 350,27 352,27 354,26 356,26 358,25 360,25 362,24 364,24 366,23 368,23 370,22 372,22 374,21 376,21 378,20 380,20"
                              stroke="#10b981" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>

                    <!-- Subtle area fill for depth -->
                    <defs>
                      <linearGradient id="areaGradient" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stop-color="#10b981" stop-opacity="0.15"/>
                        <stop offset="100%" stop-color="#10b981" stop-opacity="0.02"/>
                      </linearGradient>
                    </defs>
                    <polygon points="30,95 32,94 34,93 36,92 38,92 40,91 42,91 44,90 46,90 48,89 50,89 52,88 54,88 56,87 58,87 60,88 62,89 64,90 66,89 68,88 70,87 72,87 74,86 76,86 78,85 80,85 82,84 84,84 86,83 88,83 90,82 92,82 94,81 96,81 98,80 100,80 102,81 104,82 106,81 108,80 110,79 112,79 114,78 116,78 118,77 120,77 122,76 124,76 126,75 128,75 130,74 132,74 134,73 136,73 138,72 140,72 142,71 144,71 146,70 148,70 150,69 152,69 154,68 156,68 158,67 160,68 162,69 164,70 166,69 168,68 170,67 172,67 174,66 176,66 178,65 180,65 182,64 184,64 186,63 188,63 190,62 192,62 194,63 196,64 198,63 200,62 202,61 204,61 206,60 208,60 210,59 212,59 214,58 216,58 218,57 220,57 222,56 224,56 226,55 228,55 230,54 232,54 234,53 236,53 238,52 240,52 242,51 244,51 246,50 248,50 250,49 252,49 254,48 256,48 258,47 260,48 262,49 264,48 266,47 268,46 270,46 272,45 274,45 276,44 278,44 280,43 282,43 284,42 286,42 288,41 290,41 292,40 294,40 296,39 298,39 300,38 302,38 304,37 306,38 308,39 310,38 312,37 314,36 316,36 318,35 320,35 322,34 324,34 326,33 328,33 330,32 332,32 334,31 336,31 338,30 340,30 342,29 344,29 346,28 348,28 350,27 352,27 354,26 356,26 358,25 360,25 362,24 364,24 366,23 368,23 370,22 372,22 374,21 376,21 378,20 380,20 380,110 30,110"
                              fill="url(#areaGradient)"/>

                    <!-- Data points - minimal and subtle -->
                    <circle cx="30" cy="95" r="2" fill="#10b981"/>
                    <circle cx="380" cy="20" r="2" fill="#10b981"/>

                    <!-- X-axis labels -->
                    <text x="30" y="125" font-size="6" fill="#9ca3af">Jan 15</text>
                    <text x="107" y="125" font-size="6" fill="#9ca3af">Jan 29</text>
                    <text x="198" y="125" font-size="6" fill="#9ca3af">Feb 12</text>
                    <text x="275" y="125" font-size="6" fill="#9ca3af">Feb 26</text>
                    <text x="352" y="125" font-size="6" fill="#9ca3af">Mar 12</text>
                  </svg>
                </div>
              </div>

              <!-- Slide 3: Recommended Model Portfolio -->
              <div class="carousel-slide" :class="{ active: currentSlide === 2 }">
                <div class="model-portfolio-container">
                  <h3 class="model-portfolio-title">Recommended Model Portfolio</h3>
                  <div class="model-portfolio-content">
                    <div class="model-donut-visual"></div>
                    <div class="model-table">
                      <div class="model-row model-header">
                        <div class="model-cell">Asset Class</div>
                        <div class="model-cell">%</div>
                      </div>
                      <div class="model-row">
                        <div class="model-cell"><span class="model-dot" style="background: #ff6b9d;"></span> Flexi-Cap MF</div>
                        <div class="model-cell">50.0%</div>
                      </div>
                      <div class="model-row">
                        <div class="model-cell"><span class="model-dot" style="background: #4a90e2;"></span> Large-Cap</div>
                        <div class="model-cell">17.4%</div>
                      </div>
                      <div class="model-row">
                        <div class="model-cell"><span class="model-dot" style="background: #f5a623;"></span> Mid-Cap</div>
                        <div class="model-cell">13.6%</div>
                      </div>
                      <div class="model-row">
                        <div class="model-cell"><span class="model-dot" style="background: #4fd1c5;"></span> Fixed Deposits</div>
                        <div class="model-cell">8.1%</div>
                      </div>
                      <div class="model-row">
                        <div class="model-cell"><span class="model-dot" style="background: #9b59b6;"></span> Small-Cap</div>
                        <div class="model-cell">6.1%</div>
                      </div>
                      <div class="model-row">
                        <div class="model-cell"><span class="model-dot" style="background: #ff8c42;"></span> Gold (SGB)</div>
                        <div class="model-cell">3.6%</div>
                      </div>
                      <div class="model-row">
                        <div class="model-cell"><span class="model-dot" style="background: #ec4899;"></span> Debt MF</div>
                        <div class="model-cell">1.1%</div>
                      </div>

                    </div>
                  </div>
                  <button class="rebalance-btn">
                    <i class="pi pi-bell"></i>
                    Rebalance
                  </button>
                </div>
              </div>
            </div>

            <!-- Carousel Indicators -->
            <div class="carousel-indicators">
              <button
                v-for="i in totalSlides"
                :key="i"
                class="indicator"
                :class="{ active: currentSlide === i - 1 }"
                @click="goToSlide(i - 1)"
                :aria-label="`Go to slide ${i}`"
              ></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.hero-section {
  background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
  padding: 24px 24px 20px 24px;
  min-height: auto;
  display: flex;
  align-items: center;
}

.hero-container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
}

.hero-content {
  animation: fadeInUp 0.6s ease-out;
}

.hero-headline {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
  margin: 0 0 20px 0;
  letter-spacing: -0.02em;
}

.hero-subheadline {
  font-size: 1.125rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 24px 0;
  max-width: 540px;
}

.hero-cta-group {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.cta-primary,
.cta-secondary {
  padding: 12px 24px;
  font-size: 0.9375rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  white-space: nowrap;
}

.cta-primary {
  background: #3b82f6;
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.cta-primary:hover {
  background: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
}

.cta-secondary {
  background: white;
  color: #3b82f6;
  border: 2px solid #3b82f6;
}

.cta-secondary:hover {
  background: #eff6ff;
  transform: translateY(-2px);
}

.trust-line {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9375rem;
  color: #10b981;
  font-weight: 500;
  margin: 0;
}

.trust-line i {
  color: #10b981;
}

.hero-visual {
  display: flex;
  justify-content: center;
  align-items: center;
}

.dashboard-preview {
  width: 100%;
  max-width: 450px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  animation: fadeIn 0.8s ease-out 0.2s both;
}

.preview-header {
  background: #f3f4f6;
  padding: 16px;
  display: flex;
  gap: 8px;
}

.preview-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #d1d5db;
}

.preview-content {
  padding: 20px 24px;
  position: relative;
  min-height: 280px;
}

.carousel-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.carousel-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  opacity: 0;
  transition: opacity 0.6s ease-in-out;
  pointer-events: none;
}

.carousel-slide.active {
  opacity: 1;
  pointer-events: auto;
}

.slide-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 20px;
  text-align: center;
}

.slide-image {
  width: 100%;
  height: auto;
  border-radius: 8px;
  object-fit: contain;
}

/* Slide 1: Diversity */
.diversity-container {
  padding: 0;
}

.diversity-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 20px 0;
  text-align: center;
}

.diversity-content {
  display: grid;
  grid-template-columns: 180px 180px;
  gap: 16px;
  align-items: start;
  justify-content: center;
}

.donut-visual {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    #3b82f6 0deg 23deg,        /* Stocks: 6.33% */
    #4ade80 23deg 41deg,       /* Mutual Funds: 4.90% */
    #fbbf24 41deg 48deg,       /* PPF: 1.83% */
    #a855f7 48deg 230deg,      /* Employee Pension: 50.57% */
    #14b8a6 230deg 279deg,     /* Short Term FD: 13.53% */
    #ef4444 279deg 324deg,     /* Tax Saving FD: 12.48% */
    #ec4899 324deg 345deg,     /* Gold: 5.76% */
    #a855f7 345deg 345deg,     /* Real Estate: 0% */
    #fbbf24 345deg 360deg      /* Others: 4.60% */
  );
  position: relative;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.donut-visual::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 50%;
  height: 50%;
  background: white;
  border-radius: 50%;
}

.diversity-table {
  width: 180px;
  font-size: 0.6875rem;
}

.table-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 6px;
  padding: 3px 0;
  align-items: center;
}

.table-header {
  font-weight: 600;
  color: #4b5563;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 4px;
  margin-bottom: 2px;
}

.table-cell {
  text-align: left;
  color: #4b5563;
  display: flex;
  align-items: center;
  gap: 4px;
}

.table-cell:nth-child(2),
.table-cell:nth-child(3) {
  justify-content: flex-end;
  font-weight: 600;
  color: #1f2937;
}

.color-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Slide 2: Performance */
.performance-container {
  padding: 0;
}

.performance-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 12px 0;
  text-align: center;
}

.performance-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 8px;
}

.perf-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 10px;
}

.perf-card-title {
  font-size: 0.5625rem;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.03em;
  margin-bottom: 5px;
}

.perf-card-value {
  font-size: 1rem;
  font-weight: 700;
  color: #10b981;
}

.perf-positive {
  color: #10b981;
}

.perf-negative {
  color: #ef4444;
}

.perf-pct {
  font-size: 0.75rem;
  font-weight: 600;
}

.performance-chart-title {
  font-size: 0.625rem;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 4px;
  margin-top: 4px;
}

.trend-chart {
  width: 100%;
  height: 130px;
  display: block;
}

/* Slide 3: Model Portfolio */
.model-portfolio-container {
  padding: 0;
  display: flex;
  flex-direction: column;
}

.model-portfolio-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 20px 0;
  text-align: center;
}

.model-portfolio-content {
  display: grid;
  grid-template-columns: 180px 180px;
  gap: 16px;
  align-items: start;
  justify-content: center;
  margin-bottom: 0px;
}

.rebalance-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 2px 10px;
  font-size: 0.625rem;
  font-weight: 600;
  color: #3b82f6;
  background: #eff6ff;
  border: 1px solid #3b82f6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;
  margin-left: 0;
}

.rebalance-btn:hover {
  background: #dbeafe;
  transform: translateY(-1px);
}

.rebalance-btn i {
  font-size: 0.625rem;
}

.model-donut-visual {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    #ff6b9d 0deg 180deg,        /* Flexi-Cap: 50.0% */
    #4a90e2 180deg 243deg,      /* Large-Cap: 17.4% */
    #f5a623 243deg 292deg,      /* Mid-Cap: 13.6% */
    #4fd1c5 292deg 321deg,      /* Fixed Deposits: 8.1% */
    #9b59b6 321deg 343deg,      /* Small-Cap: 6.1% */
    #ff8c42 343deg 356deg,      /* Gold: 3.6% */
    #ec4899 356deg 360deg,      /* Debt: 1.1% */
    #d1d5db 360deg 360deg       /* Real Estate: 0.1% */
  );
  position: relative;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.model-donut-visual::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 50%;
  height: 50%;
  background: white;
  border-radius: 50%;
}

.model-table {
  width: 180px;
  font-size: 0.6875rem;
}

.model-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 6px;
  padding: 3px 0;
  align-items: center;
}

.model-header {
  font-weight: 600;
  color: #4b5563;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 4px;
  margin-bottom: 2px;
}

.model-cell {
  text-align: left;
  color: #4b5563;
  display: flex;
  align-items: center;
  gap: 4px;
}

.model-cell:nth-child(2) {
  justify-content: flex-end;
  font-weight: 600;
  color: #1f2937;
}

.model-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Carousel Indicators */
.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
}

.indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d1d5db;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.indicator.active {
  width: 24px;
  border-radius: 4px;
  background: #3b82f6;
}

.indicator:hover {
  background: #9ca3af;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 1024px) {
  .hero-container {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .hero-headline {
    font-size: 2rem;
  }

  .dashboard-preview {
    max-width: 500px;
  }
}

@media (max-width: 768px) {
  .hero-section {
    padding: 24px 20px 48px 20px;
    min-height: auto;
  }

  .hero-headline {
    font-size: 2rem;
  }

  .hero-subheadline {
    font-size: 1.125rem;
  }

  .hero-cta-group {
    flex-direction: column;
  }

  .cta-primary,
  .cta-secondary {
    width: 100%;
    justify-content: center;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .preview-content {
    padding: 24px;
    min-height: 350px;
  }
}
</style>
